/* eslint-disable no-console */
import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { useAuthContext } from "../../context/auth";
import { getCuentasBancarias } from "../../utils/movimientosBancarios";
import { client } from "../../utils/supabase";
import { Header } from "../Header";
import { Loader } from "../Loader";
import styles from "./CuentasBancarias.module.scss";

// ─── Helpers ─────────────────────────────────────────────

const formatMonto = (monto, moneda = "CLP") => {
  if (monto == null) return "-";
  const num = Number(monto);
  if (moneda === "USD") return `US$ ${num.toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
  return `$ ${num.toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
};

const formatFechaHora = (fechaStr) => {
  if (!fechaStr) return "Nunca";
  const d = new Date(fechaStr);
  return `${d.toLocaleDateString("es-CL")} ${d.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}`;
};

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// ─── Componente principal ────────────────────────────────

const CuentasBancarias = () => {
  const { pais } = useAuthContext();

  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sincronizando, setSincronizando] = useState(null); // null | 'todas' | cuentaId
  const [syncResult, setSyncResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCuenta, setEditingCuenta] = useState(null);
  const [formData, setFormData] = useState({
    numero_cuenta: "",
    tipo_cuenta: "corriente",
    moneda: "CLP",
    titular: "",
    rut_titular: "",
    clay_account_id: "",
  });

  // ─── Cargar cuentas ────────────────────────────────────

  const cargarDatos = useCallback(async () => {
    if (!pais || pais === "Region") return;
    setLoading(true);
    try {
      const data = await getCuentasBancarias(pais);
      setCuentas(data);
    } catch (err) {
      console.error("Error cargando cuentas:", err);
      toast.error("Error cargando cuentas bancarias");
    } finally {
      setLoading(false);
    }
  }, [pais]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // ─── Sincronizar ───────────────────────────────────────

  const handleSync = useCallback(
    async (cuentaNumero) => {
      const id = cuentaNumero || "todas";
      setSincronizando(id);
      setSyncResult(null);
      try {
        let url = `${SUPABASE_URL}/functions/v1/clay-import?pais=${pais}`;
        if (cuentaNumero) url += `&cuenta=${cuentaNumero}`;

        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${SUPABASE_ANON_KEY}`, "Content-Type": "application/json" },
        });
        const data = await response.json();
        if (data.error) throw new Error(data.details || data.error);

        setSyncResult(data);
        const totalNuevos = (data.resultados || []).reduce((s, r) => s + (r.nuevos || 0), 0);
        toast.success(`Sincronización completa: ${totalNuevos} movimientos nuevos`);
        await cargarDatos();
      } catch (err) {
        console.error("Error sincronizando:", err);
        toast.error("Error al sincronizar");
      } finally {
        setSincronizando(null);
      }
    },
    [pais, cargarDatos]
  );

  // ─── Modal CRUD ────────────────────────────────────────

  const openNueva = () => {
    setEditingCuenta(null);
    setFormData({
      numero_cuenta: "",
      tipo_cuenta: "corriente",
      moneda: "CLP",
      titular: "",
      rut_titular: "",
      clay_account_id: "",
    });
    setShowModal(true);
  };

  const openEditar = (cuenta) => {
    setEditingCuenta(cuenta);
    setFormData({
      numero_cuenta: cuenta.numero_cuenta || "",
      tipo_cuenta: cuenta.tipo_cuenta || "corriente",
      moneda: cuenta.moneda || "CLP",
      titular: cuenta.titular || "",
      rut_titular: cuenta.rut_titular || "",
      clay_account_id: cuenta.clay_account_id || "",
    });
    setShowModal(true);
  };

  const handleGuardar = async () => {
    if (!formData.numero_cuenta) {
      toast.error("El número de cuenta es requerido");
      return;
    }
    try {
      if (editingCuenta) {
        const { error } = await client
          .from("cuentas_bancarias")
          .update({ ...formData, updated_at: new Date().toISOString() })
          .eq("id", editingCuenta.id);
        if (error) throw error;
        toast.success("Cuenta actualizada");
      } else {
        // Buscar banco_id del país
        const { data: bancos } = await client.from("bancos").select("id").eq("pais", pais).limit(1);
        const bancoId = bancos?.[0]?.id || null;

        const { error } = await client
          .from("cuentas_bancarias")
          .insert([{ ...formData, pais, banco_id: bancoId, habilitado: true }]);
        if (error) throw error;
        toast.success("Cuenta creada");
      }
      setShowModal(false);
      await cargarDatos();
    } catch (err) {
      console.error("Error guardando cuenta:", err);
      toast.error("Error al guardar");
    }
  };

  // ─── Render ────────────────────────────────────────────

  return (
    <div className={styles.wrapper}>
      <Header title="Cuentas Bancarias" />

      <div className={styles.content}>
        {/* Toolbar */}
        <div className={styles.toolbar}>
          <button className={styles.btnPrimary} onClick={() => handleSync(null)} disabled={sincronizando !== null}>
            {sincronizando === "todas" ? "Sincronizando..." : "Sincronizar todas"}
          </button>
          <button className={styles.btnOutline} onClick={openNueva}>
            + Agregar cuenta
          </button>
        </div>

        {/* Resultado sync */}
        {syncResult && (
          <div className={styles.syncResult}>
            Última sincronización —{" "}
            {(syncResult.resultados || [])
              .map(
                (r) => `${r.cuenta} (${r.moneda}): ${r.nuevos} nuevos, saldo ${formatMonto(r.saldo_actual, r.moneda)}`
              )
              .join(" | ")}
          </div>
        )}

        {loading && <Loader />}

        {/* Cards */}
        {!loading && cuentas.length === 0 && <div className={styles.empty}>No hay cuentas bancarias registradas</div>}

        {!loading && cuentas.length > 0 && (
          <div className={styles.cuentasGrid}>
            {cuentas.map((c) => (
              <div key={c.id} className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTitulo}>
                    {c.bancos?.nombre || "Banco"} — {c.numero_cuenta}
                  </div>
                  <span className={styles.monedaChip}>{c.moneda}</span>
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.row}>
                    <span className={styles.label}>Saldo actual</span>
                    <span className={styles.saldoGrande}>{formatMonto(c.saldo_actual, c.moneda)}</span>
                  </div>
                  <div className={styles.row}>
                    <span className={styles.label}>Tipo</span>
                    <span className={styles.value}>{c.tipo_cuenta || "-"}</span>
                  </div>
                  <div className={styles.row}>
                    <span className={styles.label}>Titular</span>
                    <span className={styles.value}>{c.titular || "-"}</span>
                  </div>
                  <div className={styles.row}>
                    <span className={styles.label}>RUT</span>
                    <span className={styles.value}>{c.rut_titular || "-"}</span>
                  </div>
                  <div className={styles.row}>
                    <span className={styles.label}>Clay ID</span>
                    <span className={styles.value}>{c.clay_account_id || "-"}</span>
                  </div>
                  <div className={styles.row}>
                    <span className={styles.label}>Conexión</span>
                    <span className={styles.value}>
                      {c.clay_account_id ? (
                        <>
                          <span className={styles.statusOnline} />
                          Conectada
                        </>
                      ) : (
                        <>
                          <span className={styles.statusOffline} />
                          Sin conexión
                        </>
                      )}
                    </span>
                  </div>
                  <div className={styles.row}>
                    <span className={styles.label}>Último sync</span>
                    <span className={styles.lastSync}>{formatFechaHora(c.saldo_fecha)}</span>
                  </div>
                </div>
                <div className={styles.cardFooter}>
                  <button className={styles.btnEditar} onClick={() => openEditar(c)}>
                    Editar
                  </button>
                  <button
                    className={styles.btnSync}
                    onClick={() => handleSync(c.numero_cuenta)}
                    disabled={sincronizando !== null}
                  >
                    {sincronizando === c.numero_cuenta ? "Sincronizando..." : "Sincronizar"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal CRUD */}
      <Modal isOpen={showModal} toggle={() => setShowModal(false)}>
        <ModalHeader toggle={() => setShowModal(false)}>
          {editingCuenta ? "Editar Cuenta Bancaria" : "Nueva Cuenta Bancaria"}
        </ModalHeader>
        <ModalBody>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, fontSize: "0.82rem" }}>
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Número de cuenta *</label>
              <input
                type="text"
                value={formData.numero_cuenta}
                onChange={(e) => setFormData({ ...formData, numero_cuenta: e.target.value })}
                style={{ width: "100%", height: 36, padding: "4px 10px", border: "1px solid #ced4da", borderRadius: 4 }}
              />
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Tipo</label>
                <select
                  value={formData.tipo_cuenta}
                  onChange={(e) => setFormData({ ...formData, tipo_cuenta: e.target.value })}
                  style={{
                    width: "100%",
                    height: 36,
                    padding: "4px 10px",
                    border: "1px solid #ced4da",
                    borderRadius: 4,
                  }}
                >
                  <option value="corriente">Corriente</option>
                  <option value="vista">Vista</option>
                  <option value="ahorro">Ahorro</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Moneda</label>
                <select
                  value={formData.moneda}
                  onChange={(e) => setFormData({ ...formData, moneda: e.target.value })}
                  style={{
                    width: "100%",
                    height: 36,
                    padding: "4px 10px",
                    border: "1px solid #ced4da",
                    borderRadius: 4,
                  }}
                >
                  <option value="CLP">CLP</option>
                  <option value="USD">USD</option>
                  <option value="PEN">PEN</option>
                </select>
              </div>
            </div>
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Titular</label>
              <input
                type="text"
                value={formData.titular}
                onChange={(e) => setFormData({ ...formData, titular: e.target.value })}
                style={{ width: "100%", height: 36, padding: "4px 10px", border: "1px solid #ced4da", borderRadius: 4 }}
              />
            </div>
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>RUT titular</label>
              <input
                type="text"
                value={formData.rut_titular}
                onChange={(e) => setFormData({ ...formData, rut_titular: e.target.value })}
                style={{ width: "100%", height: 36, padding: "4px 10px", border: "1px solid #ced4da", borderRadius: 4 }}
              />
            </div>
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Clay Account ID</label>
              <input
                type="text"
                value={formData.clay_account_id}
                onChange={(e) => setFormData({ ...formData, clay_account_id: e.target.value })}
                placeholder="Ej: W4zv3Xmvckz6m7cYJ"
                style={{ width: "100%", height: 36, padding: "4px 10px", border: "1px solid #ced4da", borderRadius: 4 }}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button color="dark" size="sm" onClick={handleGuardar}>
            {editingCuenta ? "Guardar cambios" : "Crear cuenta"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CuentasBancarias;
